import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Play, RefreshCw } from "lucide-react";

const fmt = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString("it-IT") : "");

// Elenco di chi verrebbe tolto alla data X + "Esegui ora" (anteprima e doppia conferma)
export const PreregRemoval = ({ year, cfg, onRan }) => {
  const [pv, setPv] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/prereg/removal-preview", { params: { year } }).then((r) => setPv(r.data)).catch(() => {});
  useEffect(() => { load(); }, [year, cfg?.date_x, cfg?.date_x_done]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/prereg/run-now", { year, expected: pv.count });
      toast.success(`${data.removed} allievi tolti dal corso`); setStep(0); onRan(data); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); load(); } finally { setBusy(false); }
  };
  if (!pv) return null;
  return (
    <div className="space-y-2 rounded-lg border border-border p-3" data-testid="prereg-removal">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-semibold">Verrebbero tolti dal corso {cfg?.date_x ? `dopo il ${fmt(cfg.date_x)}` : "(data X non impostata)"}: <span data-testid="prereg-removal-count">{pv.count}</span></p>
        <Button size="sm" variant="ghost" onClick={load} data-testid="prereg-removal-refresh"><RefreshCw size={14} /></Button>
      </div>
      {cfg?.date_x_done && <p className="text-xs text-muted-foreground" data-testid="prereg-done">Rimozione già eseguita per la data {fmt(cfg.date_x_done)}.</p>}
      {pv.restored_kept > 0 && <p className="text-xs text-muted-foreground" data-testid="prereg-restored-kept">{pv.restored_kept} "Da confermare" rimessi nel corso dal master: non verranno tolti.</p>}
      {pv.by_course.map((c) => (
        <div key={c.course} className="text-xs" data-testid={`prereg-removal-course-${c.course}`}>
          <p className="font-medium">{c.course} · {c.students.length}</p>
          <p className="text-muted-foreground break-words">{c.students.map((s) => s.name || s.email).join(", ")}</p>
        </div>
      ))}
      <Button size="sm" variant="outline" data-testid="prereg-run-now" disabled={pv.count === 0} onClick={() => setStep(1)} className="text-destructive hover:text-destructive"><Play size={14} className="mr-1.5" /> Esegui ora</Button>
      <Dialog open={step > 0} onOpenChange={(o) => !o && setStep(0)}>
        <DialogContent data-testid="prereg-run-dialog" className="w-[calc(100%-1.5rem)] rounded-lg">
          <DialogHeader><DialogTitle>Togliere ora {pv.count} allievi dal corso?</DialogTitle>
            <DialogDescription>{step === 1
              ? "Vengono tolti solo gli allievi ancora \"Da confermare\" (nessuna risposta e nessun modulo inviato). Le preiscrizioni dell'anno verranno chiuse. Account e anno di partenza non vengono toccati. Nessuna email."
              : `Conferma definitiva: ${pv.count} allievi in ${pv.by_course.length} corsi. Potrai rimetterli nel corso dal registro.`}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStep(0)}>Annulla</Button>
            {step === 1 ? <Button data-testid="prereg-run-next" variant="destructive" onClick={() => setStep(2)}>Continua</Button>
              : <Button data-testid="prereg-run-confirm" variant="destructive" disabled={busy} onClick={run}>Togli {pv.count} allievi</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
